import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect } from "react";
import { fetchCartItems, fetchCartPrices, fetchCleanCartItems, removeItem, removeItems, selectCartPending, selectFreeGiftsErrors, selectProductsInCart, selectQtyPending, updateItemQuantity, updateTradeInOrderAttributes, useDispatch, useSelector, } from "@jti/store";
import { useConfig, useTranslations } from "@jti/ui";
import { CartItem } from "../../components/CartItem";
import { BundleItem } from "../../components/CartItem/BundleItem";
import { CartItemList } from "../../components/CartItemList";
import { LoadingWrapper } from "../../components/LoadingWrapper";
import { useGtm } from "../../hooks/gtm/useGtm";
import { useFreeGifts } from "../../hooks/useFreeGifts";
import { GiftBlock } from "./components";
import styles from "./CartItems.module.scss";
export const CartItems = ({ isLimitEnabled = false, warningStatus, ageVerificationMessage }) => {
    const { __ } = useTranslations();
    const config = useConfig();
    const dispatch = useDispatch();
    const cartId = useSelector((state) => state.cart.id);
    const productsInCart = useSelector(selectProductsInCart);
    const freeTrialCart = useSelector((state) => state.freeTrialCart);
    const itemsPending = useSelector(selectCartPending);
    const qtyPending = useSelector(selectQtyPending);
    const freeGiftsErrors = useSelector(selectFreeGiftsErrors);
    const { dispatchGtmPageViewEvent } = useGtm();
    const { giftsByProduct, selectedFreeGifts, setSelectedFreeGiftQuantity } = useFreeGifts();
    useEffect(() => {
        if (cartId) {
            dispatch(fetchCleanCartItems(cartId));
        }
    }, [cartId, dispatch]);
    const handleQuantityChange = useCallback(async (itemId, quantity) => {
        if (!cartId)
            return;
        const item = productsInCart.find(({ id }) => id === itemId);
        if (!item)
            return;
        const giftItems = productsInCart.filter((product) => product.isGift && product.parentGiftId === item.id);
        for (const giftItem of giftItems) {
            if (giftItem.id) {
                await dispatch(removeItem({ cartId, itemId: giftItem.id }));
            }
        }
        const relatedGifts = selectedFreeGifts[item.id];
        if (relatedGifts) {
            Object.keys(relatedGifts).forEach((giftSku) => {
                setSelectedFreeGiftQuantity({ parentGiftId: item.id, giftSku, quantity: 0 });
            });
        }
        await dispatch(updateItemQuantity({ cartId, itemId, quantity }));
        await dispatch(fetchCartItems(cartId));
        await dispatch(fetchCartPrices(cartId));
        if (item.tradeIn) {
            dispatch(updateTradeInOrderAttributes(null));
        }
    }, [cartId, productsInCart, selectedFreeGifts, dispatch, setSelectedFreeGiftQuantity]);
    const handleRemoveItem = useCallback(async (itemId) => {
        if (!cartId)
            return;
        const cartItem = productsInCart.find(({ id }) => id === itemId);
        await dispatch(removeItem({ cartId, itemId }));
        dispatch(fetchCartItems(cartId));
        if (cartItem?.tradeIn) {
            dispatch(updateTradeInOrderAttributes(null));
        }
    }, [cartId, dispatch]);
    const handleRemoveItems = useCallback(async (itemIds) => {
        if (!cartId)
            return;
        await dispatch(removeItems({ cartId, itemIds }));
        dispatch(fetchCartPrices(cartId));
        const tradeInItem = productsInCart.find((item) => item.tradeIn);
        if (tradeInItem) {
            dispatch(updateTradeInOrderAttributes(null));
        }
    }, [cartId, dispatch]);
    useEffect(() => {
        if (!itemsPending && !freeTrialCart?.order?.orderNumber) {
            dispatchGtmPageViewEvent("cart");
        }
    }, [itemsPending]);
    return (_jsxs(LoadingWrapper, { loading: itemsPending, children: [ageVerificationMessage && _jsx("div", { className: styles.ageVerificationBlock, children: ageVerificationMessage }), _jsx(CartItemList, { items: productsInCart, testId: "regular-cart-list", emptyMessage: __("cart.items.na"), onRemoveItems: handleRemoveItems, renderItem: (item) => {
                    if (item.isGift)
                        return null;
                    const productGifts = giftsByProduct.find((g) => g.id === item.id);
                    const hasFreeGifts = !!productGifts?.freeGifts?.length;
                    const hasMultipleProductsWithGifts = giftsByProduct.length > 1;
                    const hasErrors = freeGiftsErrors?.some((freeGiftError) => freeGiftError.parentSku === item.sku && freeGiftError.parentId === item.id);
                    const giftBlock = (hasFreeGifts || hasErrors) && cartId ? (_jsx(GiftBlock, { parentItemSku: item.sku, parentItemId: item.id, limit: productGifts?.limit ?? 0, freeGifts: productGifts?.freeGifts ?? [], selectedFreeGifts: selectedFreeGifts, setSelectedFreeGiftQuantity: setSelectedFreeGiftQuantity, hasMultipleProductsWithGifts: hasMultipleProductsWithGifts })) : null;
                    const cartItem = item.type_id === "bundle" && config.displayBundleSelectedItems ? (_jsx(BundleItem, { item: item, qtyPending: qtyPending, blockQuantityAdd: warningStatus, onQuantityChange: handleQuantityChange, onRemoveClick: handleRemoveItem }, item.id)) : (_jsx(CartItem, { item: item, qtyPending: qtyPending, isLimitEnabled: isLimitEnabled, blockQuantityAdd: warningStatus, onQuantityChange: handleQuantityChange, onRemoveClick: handleRemoveItem }, item.id));
                    return (_jsxs("div", { className: styles.cartItemWrapper, children: [cartItem, giftBlock] }, item.id));
                } })] }));
};
//# sourceMappingURL=CartItems.js.map